import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './AuditLog.css';

const AuditLog = ({ documentId, onClose }) => {
  const { token, isAuthenticated } = useAuth();
  const [auditEntries, setAuditEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get proper authentication headers
  const getAuthHeaders = () => {
    // Priority: 1. User JWT token, 2. Environment variable, 3. Default fallback
    const authToken = (isAuthenticated() && token) 
      ? token 
      : (process.env.REACT_APP_API_AUTH_TOKEN || '12345');
      
    return {
      'Authorization': `Bearer ${authToken}`
    };
  };

  useEffect(() => {
    const fetchAuditLog = async () => {
      try {
        const response = await axios.get(`/documents/${documentId}/reviews`, {
          headers: getAuthHeaders()
        });
        if (response.status === 200) {
          setAuditEntries(response.data);
          setLoading(false);
        } else {
          setError('Error fetching audit log: ' + response.statusText);
          setLoading(false);
        }
      } catch (err) {
        setError('Error fetching audit log: ' + err.message);
        setLoading(false);
      }
    };

    if (documentId) {
      fetchAuditLog();
    }
  }, [documentId]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    if (!status) return '#6c757d'; // Default color for null/undefined status
    switch (status.toLowerCase()) {
      case 'approved': return '#28a745';
      case 'verifying': return '#17a2b8';
      case 'processing': return '#ffc107';
      case 'failed': return '#dc3545';
      case 'rejected': return '#dc3545';
      case 'review_required': return '#fd7e14';
      case 'opened': return '#6c757d';
      case 'processed': return '#28a745';
      case 'pending': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  // Helper function to get status transitions from the status_transitions field
  const getStatusTransitions = (statusTransitions) => {
    if (!statusTransitions || !Array.isArray(statusTransitions)) return [];
    
    return statusTransitions.map(transition => ({
      timestamp: transition.timestamp,
      fromStatus: transition.from_status || null,  // Handle null for initial status
      toStatus: transition.to_status,
      transitionComments: transition.transition_comments,
      reviewer: transition.reviewer
    }));
  };

  // Helper function to extract main action from comments
  const extractMainAction = (comments) => {
    if (!comments) return '';
    
    const lines = comments.split('\n');
    // Find the first line that's not a status history line
    for (const line of lines) {
      if (line.trim() && !line.match(/^\[(\d{2}:\d{2}:\d{2})\] Status:/)) {
        return line.trim();
      }
    }
    
    return '';
  };

  // Helper function to parse action results from transition comments
  const parseActionResults = (comments, toStatus) => {
    if (!comments) return null;
    
    const results = {
      summary: '',
      details: [],
      hasStructuredResults: false
    };
    
    // Parse different types of actions based on status and content
    if (toStatus === 'verifying' || comments.includes('verification')) {
      if (comments.includes('validation issues found')) {
        // Extract validation results
        const lines = comments.split('\n');
        results.summary = lines[0]; // First line is usually the summary
        results.hasStructuredResults = true;
        
        // Extract individual validation issues
        for (const line of lines) {
          if (line.includes('Employee ') && line.includes(':')) {
            results.details.push({
              type: 'validation_issue',
              content: line.trim()
            });
          }
        }
        
        if (comments.includes('User corrections:')) {
          const correctionMatch = comments.match(/User corrections: ([^\n]+)/);
          if (correctionMatch) {
            results.details.push({
              type: 'user_correction',
              content: correctionMatch[1]
            });
          }
        }
      } else if (comments.includes('verification completed successfully')) {
        results.summary = 'All employee data validated successfully';
        results.hasStructuredResults = true;
        results.details.push({
          type: 'success',
          content: 'All employees passed validation checks'
        });
      }
    } else if (toStatus === 'processing') {
      if (comments.includes('approved for employee processing')) {
        results.summary = 'Document approved for processing';
        results.hasStructuredResults = true;
        results.details.push({
          type: 'approval',
          content: 'Document cleared for employee data entry'
        });
      }
    } else if (toStatus === 'rejected') {
      results.summary = 'Document rejected';
      results.hasStructuredResults = true;
      
      if (comments.includes('Rejection reason:')) {
        const reasonMatch = comments.match(/Rejection reason: ([^\n]+)/);
        if (reasonMatch) {
          results.details.push({
            type: 'rejection_reason',
            content: reasonMatch[1]
          });
        }
      }
    } else if (toStatus === 'approved' || toStatus === 'processed') {
      if (comments.includes('employees processed successfully')) {
        const employeeCountMatch = comments.match(/(\d+) employees processed successfully/);
        if (employeeCountMatch) {
          results.summary = `Successfully processed ${employeeCountMatch[1]} employees`;
          results.hasStructuredResults = true;
          results.details.push({
            type: 'processing_success',
            content: `${employeeCountMatch[1]} employee records entered into S3Lion system`
          });
        }
      } else if (comments.includes('processing failed')) {
        const lines = comments.split('\n');
        results.summary = lines[0];
        results.hasStructuredResults = true;
        
        // Extract individual processing errors
        for (const line of lines) {
          if (line.includes('Employee ') && line.includes(':')) {
            results.details.push({
              type: 'processing_error',
              content: line.trim()
            });
          }
        }
      }
    }
    
    // If no structured results found, use original comment as summary
    if (!results.hasStructuredResults) {
      results.summary = comments;
    }
    
    return results;
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Audit Log - Document {documentId}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p>Loading audit log...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Audit Log - Document {documentId}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="error-message">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content audit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Document Review History - Document {documentId}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {auditEntries.length === 0 ? (
            <p>No review history recorded for this document.</p>
          ) : (
            <div className="audit-timeline">
              {auditEntries.map((entry, entryIndex) => {
                // Get status transitions from the status_transitions field
                const statusTransitions = getStatusTransitions(entry.status_transitions);
                const mainAction = extractMainAction(entry.comments);
                
                // Create timeline entries for each status transition
                const timelineEntries = [];
                
                // Determine the last status and current status from transitions
                let fromStatus = entry.previous_status;
                let currentStatus = entry.new_status;
                
                if (statusTransitions.length > 0) {
                  // Get the most recent transition (last in array)
                  const latestTransition = statusTransitions[statusTransitions.length - 1];
                  currentStatus = latestTransition.toStatus;
                  fromStatus = latestTransition.fromStatus;  // Use the from_status of the latest transition
                }
                
                // Add the main entry (overall review summary showing last → current)
                timelineEntries.push({
                  type: 'main',
                  id: `${entry.review_id}-main`,
                  date: entry.review_date,
                  reviewer: entry.reviewer_id,
                  reviewId: entry.review_id,
                  fromStatus: fromStatus,  // Last status before current
                  toStatus: currentStatus,  // Current status
                  action: mainAction,
                  correctionComments: entry.correction_comments,
                  duration: entry.review_duration,
                  latestTransitionComments: statusTransitions.length > 0 ? statusTransitions[statusTransitions.length - 1].transitionComments : null
                });
                
                // Add individual status transitions from the transitions array
                statusTransitions.forEach((transition, transIndex) => {
                  // Parse action results for each transition
                  const actionResults = parseActionResults(transition.transitionComments, transition.toStatus);
                  
                  timelineEntries.push({
                    type: 'transition',
                    id: `${entry.review_id}-trans-${transIndex}`,
                    date: transition.timestamp,
                    reviewer: transition.reviewer,
                    fromStatus: transition.fromStatus,
                    toStatus: transition.toStatus,
                    transitionComments: transition.transitionComments,
                    actionResults: actionResults,  // Add parsed results
                    sequenceNumber: transIndex + 1  // Add sequence number starting from 1
                  });
                });
                
                return timelineEntries;
              }).flat().sort((a, b) => {
                // Sort by date, but keep main entries at the top for each review
                if (a.type === 'main' && b.type === 'main') {
                  return new Date(b.date) - new Date(a.date); // Main entries: newest first
                }
                if (a.type === 'main') return -1; // Main entry goes first
                if (b.type === 'main') return 1;  // Main entry goes first
                return new Date(a.date) - new Date(b.date); // Transitions: oldest first (chronological)
              }).map((timelineEntry, index, sortedEntries) => {
                // Check if we need to add a separator after main entries
                const isLastMainEntry = timelineEntry.type === 'main' && 
                                      (index === sortedEntries.length - 1 || sortedEntries[index + 1].type === 'transition');
                
                return (
                  <React.Fragment key={timelineEntry.id}>
                    <div className={`audit-entry ${timelineEntry.type === 'transition' ? 'audit-transition' : 'audit-main'}`}>
                  <div className="audit-timeline-dot"></div>
                  <div className="audit-content">
                    <div className="audit-header">
                      <div className="audit-date">
                        {formatDate(timelineEntry.date)}
                      </div>
                      <div className="audit-reviewer">
                        {timelineEntry.type === 'main' ? 
                          `Current Status - Review Session #${timelineEntry.reviewId}` :
                          timelineEntry.fromStatus === null ?
                            `Initial Status by ${timelineEntry.reviewer}` :
                            `Action #${timelineEntry.sequenceNumber} by ${timelineEntry.reviewer}`
                        }
                      </div>
                    </div>
                    <div className="audit-status-change">
                      {timelineEntry.type === 'main' ? (
                        // For main entry, show the full transition (last -> current)
                        <>
                          {timelineEntry.fromStatus && (
                            <>
                              <span 
                                className="status-badge"
                                style={{ backgroundColor: getStatusColor(timelineEntry.fromStatus) }}
                              >
                                {timelineEntry.fromStatus}
                              </span>
                              <span className="arrow">→</span>
                            </>
                          )}
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(timelineEntry.toStatus) }}
                          >
                            {timelineEntry.toStatus || 'unknown'}
                          </span>
                        </>
                      ) : (
                        // For action entries, only show the status it changed to
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(timelineEntry.toStatus) }}
                        >
                          {timelineEntry.toStatus || 'unknown'}
                        </span>
                      )}
                    </div>
                    
                    {timelineEntry.type === 'main' && (
                      <>
                        {/* Show the complete details from the latest transition */}
                        {timelineEntry.latestTransitionComments && (
                          <div className="audit-action">
                            <strong>Details:</strong> {timelineEntry.latestTransitionComments}
                          </div>
                        )}
                        {timelineEntry.action && !timelineEntry.latestTransitionComments && (
                          <div className="audit-action">
                            <strong>Action:</strong> {timelineEntry.action}
                          </div>
                        )}
                        {timelineEntry.correctionComments && (
                          <div className="audit-corrections">
                            <strong>User Corrections:</strong> {timelineEntry.correctionComments}
                          </div>
                        )}
                        {timelineEntry.duration > 0 && (
                          <div className="audit-duration">
                            <strong>Processing Time:</strong> {timelineEntry.duration} ms
                          </div>
                        )}
                      </>
                    )}
                    
                    {timelineEntry.type === 'transition' && timelineEntry.actionResults && (
                      <div className="audit-transition-details">
                        {timelineEntry.actionResults.hasStructuredResults ? (
                          <>
                            <div className="audit-action-summary">
                              <strong>Summary:</strong> {timelineEntry.actionResults.summary}
                            </div>
                            {timelineEntry.actionResults.details.length > 0 && (
                              <div className="audit-action-results">
                                <strong>Results:</strong>
                                <ul className="action-results-list">
                                  {timelineEntry.actionResults.details.map((detail, index) => (
                                    <li key={index} className={`result-item result-${detail.type}`}>
                                      {detail.content}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <strong>Details:</strong> {timelineEntry.transitionComments}
                          </>
                        )}
                      </div>
                    )}
                      </div>
                    </div>
                    
                    {/* Add separator line after main entry if there are transition entries following */}
                    {isLastMainEntry && (
                      <div className="audit-separator">
                        <div className="separator-line"></div>
                        <div className="separator-text">Document Actions History</div>
                        <div className="separator-line"></div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLog;